import { Button, Separator } from '@ibsys/kit';
import { BoldIcon, ItalicIcon, ListIcon, ListOrderedIcon, RemoveFormattingIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { RichTextFormats, RichTextListType } from './types';

type Props = {
  formats: RichTextFormats;
  disabled?: boolean;
  onToggleFormat: (name: 'bold' | 'italic') => void;
  onToggleList: (type: RichTextListType) => void;
  onClearFormat: () => void;
};

/** Кнопка тулбара: подсвечивается, когда формат активен в текущем выделении */
const ToolbarButton: FC<{
  isActive?: boolean;
  label: string;
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ isActive, label, disabled, icon, onClick }) => (
  <Button
    variant="ghost"
    size="icon"
    type="button"
    aria-label={label}
    // Приводим к булеву типу: Quill отдаёт неактивный формат как undefined, и атрибут пропал бы из разметки
    aria-pressed={Boolean(isActive)}
    tooltip={label}
    disabled={disabled}
    icon={icon}
    className={cn('size-7', isActive && 'bg-accent text-accent-foreground')}
    // Не отдаём фокус кнопке — иначе Quill теряет выделение и форматировать становится нечего
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  />
);

/**
 * Панель форматирования {@link RichTextEditor}.
 *
 * Набор кнопок ограничен форматами, которые переживают санитизацию при просмотре
 * (см. `RICH_TEXT_ALLOWED_TAGS`): подчёркивание и ссылки сюда сознательно не входят.
 */
export const RichTextEditorToolbar: FC<Props> = ({
  formats,
  disabled,
  onToggleFormat,
  onToggleList,
  onClearFormat,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex items-center gap-0.5">
      <ToolbarButton
        isActive={formats.bold}
        label={t('richTextEditor.bold')}
        disabled={disabled}
        icon={<BoldIcon className="size-4" />}
        onClick={() => onToggleFormat('bold')}
      />
      <ToolbarButton
        isActive={formats.italic}
        label={t('richTextEditor.italic')}
        disabled={disabled}
        icon={<ItalicIcon className="size-4" />}
        onClick={() => onToggleFormat('italic')}
      />

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        isActive={formats.list === 'bullet'}
        label={t('richTextEditor.bulletList')}
        disabled={disabled}
        icon={<ListIcon className="size-4" />}
        onClick={() => onToggleList('bullet')}
      />
      <ToolbarButton
        isActive={formats.list === 'ordered'}
        label={t('richTextEditor.orderedList')}
        disabled={disabled}
        icon={<ListOrderedIcon className="size-4" />}
        onClick={() => onToggleList('ordered')}
      />

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        label={t('richTextEditor.clearFormat')}
        disabled={disabled}
        icon={<RemoveFormattingIcon className="size-4" />}
        onClick={onClearFormat}
      />
    </div>
  );
};
